import ahrefsApiKeyStep1 from '@/assets/images/ahrefs_api_key_step1.png';
import ahrefsApiKeyStep2 from '@/assets/images/ahrefs_api_key_step2.png';
import ahrefsApiKeyStep3 from '@/assets/images/ahrefs_api_key_step3.png';
import dingtalkCustomAppStep1 from '@/assets/images/dingtalk_custom_app_step1.svg';
import dingtalkCustomAppStep2 from '@/assets/images/dingtalk_custom_app_step2.svg';
import dingtalkCustomAppStep3 from '@/assets/images/dingtalk_custom_app_step3.svg';
import dingtalkMcpStep1 from '@/assets/images/dingtalk_mcp_step1.svg';
import dingtalkMcpStep2 from '@/assets/images/dingtalk_mcp_step2.svg';
import dingtalkMcpStep3 from '@/assets/images/dingtalk_mcp_step3.svg';
import feishuCustomAppStep1 from '@/assets/images/feishu_custom_app_step1.png';
import feishuCustomAppStep2 from '@/assets/images/feishu_custom_app_step2.png';
import feishuCustomAppStep3 from '@/assets/images/feishu_custom_app_step3.png';
import goaffproApiKeyStep1 from '@/assets/images/goaffpro_api_key_step1.png';
import goaffproApiKeyStep2 from '@/assets/images/goaffpro_api_key_step2.png';
import goaffproApiKeyStep3 from '@/assets/images/goaffpro_api_key_step3.png';
import looxApiCredentialsStep1 from '@/assets/images/loox_api_credentials_step1.png';
import looxApiCredentialsStep2 from '@/assets/images/loox_api_credentials_step2.png';
import looxApiCredentialsStep3 from '@/assets/images/loox_api_credentials_step3.png';
import omnisendApiKeyStep1 from '@/assets/images/omnisend_api_key_step1.png';
import omnisendApiKeyStep2 from '@/assets/images/omnisend_api_key_step2.png';
import omnisendApiKeyStep3 from '@/assets/images/omnisend_api_key_step3.png';
import okendoApiCredentialsStep1 from '@/assets/images/okendo_api_credentials_step1.png';
import okendoApiCredentialsStep2 from '@/assets/images/okendo_api_credentials_step2.png';
import okendoApiCredentialsStep3 from '@/assets/images/okendo_api_credentials_step3.png';
import prestashopWebserviceStep1 from '@/assets/images/prestashop_webservice_step1.png';
import prestashopWebserviceStep2 from '@/assets/images/prestashop_webservice_step2.png';
import prestashopWebserviceStep3 from '@/assets/images/prestashop_webservice_step3.png';
import shopUrl1 from '@/assets/images/shopify_url1.png';
import shopUrl2 from '@/assets/images/shopify_url2.png';
import trustwillApiKeyStep1 from '@/assets/images/trustwill_api_key_step1.png';
import trustwillApiKeyStep2 from '@/assets/images/trustwill_api_key_step2.png';
import trustwillApiKeyStep3 from '@/assets/images/trustwill_api_key_step3.png';
import uppromoteApiKeyStep1 from '@/assets/images/uppromote_api_key_step1.png';
import uppromoteApiKeyStep2 from '@/assets/images/uppromote_api_key_step2.png';
import uppromoteApiKeyStep3 from '@/assets/images/uppromote_api_key_step3.png';
import { useTranslation } from '@client-app/i18n';
import { ImageSquare, Info, MagnifyingGlassPlus, X } from '@phosphor-icons/react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { WEBSITE_OVERLAY_LAYERS } from '../../../overlayLayers';

type GuideImage = {
    src?: string;
    altKey: string;
};

type IntegrationConnectGuideFlow = 'parameterInput';
type IntegrationConnectGuideCopyKey =
    | 'shopify'
    | 'bigCommerce'
    | 'feishu'
    | 'dingtalk'
    | 'dingtalkMcp'
    | 'attribuly'
    | 'ahrefs'
    | 'omnisend'
    | 'figma'
    | 'okendo'
    | 'prestashop'
    | 'goaffpro'
    | 'uppromote'
    | 'loox'
    | 'trustwill'
    | 'noxinfluencer'
    | 'semrush'
    | 'wooCommerce'
    | 'magento'
    | 'goaffpro'
    | 'uppromote'
    | 'loox'
    | 'trustwill'
    | 'noxinfluencer';

type IntegrationConnectGuideStep = {
    key: string;
    image: GuideImage;
    showCopy?: boolean;
    showImage?: boolean;
};

export type IntegrationConnectGuideConfig = {
    flow: IntegrationConnectGuideFlow;
    copyKey: IntegrationConnectGuideCopyKey;
    toolName: string;
    steps: IntegrationConnectGuideStep[];
    action?: {
        href: string;
        labelKey: string;
    };
};

type IntegrationConnectGuideTemplate = Omit<IntegrationConnectGuideConfig, 'flow' | 'toolName'>;

const SHOPIFY_IMAGE_GUIDES: GuideImage[] = [
    {
        src: shopUrl1,
        altKey: 'website.connectShopify.helpStepOneAlt',
    },
    {
        src: shopUrl2,
        altKey: 'website.connectShopify.helpStepTwoAlt',
    },
];

const FEISHU_IMAGE_GUIDES: GuideImage[] = [
    {
        src: feishuCustomAppStep1,
        altKey: 'website.integrationConnectGuides.feishu.steps.openPlatform.imageAlt',
    },
    {
        src: feishuCustomAppStep2,
        altKey: 'website.integrationConnectGuides.feishu.steps.copyCredentials.imageAlt',
    },
    {
        src: feishuCustomAppStep3,
        altKey: 'website.integrationConnectGuides.feishu.steps.addPermissions.imageAlt',
    },
];

const DINGTALK_IMAGE_GUIDES: GuideImage[] = [
    {
        src: dingtalkCustomAppStep1,
        altKey: 'website.integrationConnectGuides.dingtalk.steps.createInternalApp.imageAlt',
    },
    {
        src: dingtalkCustomAppStep2,
        altKey: 'website.integrationConnectGuides.dingtalk.steps.configureCallback.imageAlt',
    },
    {
        src: dingtalkCustomAppStep3,
        altKey: 'website.integrationConnectGuides.dingtalk.steps.enablePermissions.imageAlt',
    },
];

const DINGTALK_MCP_IMAGE_GUIDES: GuideImage[] = [
    {
        src: dingtalkMcpStep1,
        altKey: 'website.integrationConnectGuides.dingtalkMcp.steps.chooseService.imageAlt',
    },
    {
        src: dingtalkMcpStep2,
        altKey: 'website.integrationConnectGuides.dingtalkMcp.steps.enableService.imageAlt',
    },
    {
        src: dingtalkMcpStep3,
        altKey: 'website.integrationConnectGuides.dingtalkMcp.steps.copyConfiguration.imageAlt',
    },
];

const AHREFS_IMAGE_GUIDES: GuideImage[] = [
    {
        src: ahrefsApiKeyStep1,
        altKey: 'website.integrationConnectGuides.ahrefs.steps.openApiKeys.imageAlt',
    },
    {
        src: ahrefsApiKeyStep2,
        altKey: 'website.integrationConnectGuides.ahrefs.steps.createKey.imageAlt',
    },
    {
        src: ahrefsApiKeyStep3,
        altKey: 'website.integrationConnectGuides.ahrefs.steps.pasteKey.imageAlt',
    },
];

const OMNISEND_IMAGE_GUIDES: GuideImage[] = [
    {
        src: omnisendApiKeyStep1,
        altKey: 'website.integrationConnectGuides.omnisend.steps.openApiKeys.imageAlt',
    },
    {
        src: omnisendApiKeyStep2,
        altKey: 'website.integrationConnectGuides.omnisend.steps.createKey.imageAlt',
    },
    {
        src: omnisendApiKeyStep3,
        altKey: 'website.integrationConnectGuides.omnisend.steps.copyKey.imageAlt',
    },
];

const OKENDO_IMAGE_GUIDES: GuideImage[] = [
    {
        src: okendoApiCredentialsStep1,
        altKey: 'website.integrationConnectGuides.okendo.steps.openCredentials.imageAlt',
    },
    {
        src: okendoApiCredentialsStep2,
        altKey: 'website.integrationConnectGuides.okendo.steps.copyUserIdAndApiKey.imageAlt',
    },
    {
        src: okendoApiCredentialsStep3,
        altKey: 'website.integrationConnectGuides.okendo.steps.pasteCredentials.imageAlt',
    },
];

const PRESTASHOP_IMAGE_GUIDES: GuideImage[] = [
    {
        src: prestashopWebserviceStep1,
        altKey: 'website.integrationConnectGuides.prestashop.steps.openWebservice.imageAlt',
    },
    {
        src: prestashopWebserviceStep2,
        altKey: 'website.integrationConnectGuides.prestashop.steps.createKey.imageAlt',
    },
    {
        src: prestashopWebserviceStep3,
        altKey: 'website.integrationConnectGuides.prestashop.steps.copyKey.imageAlt',
    },
];

const GOAFFPRO_IMAGE_GUIDES: GuideImage[] = [
    {
        src: goaffproApiKeyStep1,
        altKey: 'website.integrationConnectGuides.goaffpro.steps.openApiKeys.imageAlt',
    },
    {
        src: goaffproApiKeyStep2,
        altKey: 'website.integrationConnectGuides.goaffpro.steps.copyAdminAccessToken.imageAlt',
    },
    {
        src: goaffproApiKeyStep3,
        altKey: 'website.integrationConnectGuides.goaffpro.steps.pasteAdminAccessToken.imageAlt',
    },
];

const UPPROMOTE_IMAGE_GUIDES: GuideImage[] = [
    {
        src: uppromoteApiKeyStep1,
        altKey: 'website.integrationConnectGuides.uppromote.steps.openIntegrationSettings.imageAlt',
    },
    {
        src: uppromoteApiKeyStep2,
        altKey: 'website.integrationConnectGuides.uppromote.steps.getApiKey.imageAlt',
    },
    {
        src: uppromoteApiKeyStep3,
        altKey: 'website.integrationConnectGuides.uppromote.steps.pasteApiKey.imageAlt',
    },
];

const LOOX_IMAGE_GUIDES: GuideImage[] = [
    {
        src: looxApiCredentialsStep1,
        altKey: 'website.integrationConnectGuides.loox.steps.openApiKeys.imageAlt',
    },
    {
        src: looxApiCredentialsStep2,
        altKey: 'website.integrationConnectGuides.loox.steps.copyCredentials.imageAlt',
    },
    {
        src: looxApiCredentialsStep3,
        altKey: 'website.integrationConnectGuides.loox.steps.pasteCredentials.imageAlt',
    },
];

const TRUSTWILL_IMAGE_GUIDES: GuideImage[] = [
    {
        src: trustwillApiKeyStep1,
        altKey: 'website.integrationConnectGuides.trustwill.steps.openDeveloperTools.imageAlt',
    },
    {
        src: trustwillApiKeyStep2,
        altKey: 'website.integrationConnectGuides.trustwill.steps.copyApiKey.imageAlt',
    },
    {
        src: trustwillApiKeyStep3,
        altKey: 'website.integrationConnectGuides.trustwill.steps.pasteApiKey.imageAlt',
    },
];

const PLACEHOLDER_IMAGE_GUIDE: GuideImage = {
    altKey: 'website.integrationConnectGuides.placeholderImageAlt',
};

const createPlaceholderSteps = (
    stepKeys: string[],
    options: Pick<IntegrationConnectGuideStep, 'showImage'> = {}
): IntegrationConnectGuideStep[] => stepKeys.map((key) => ({
    key,
    image: PLACEHOLDER_IMAGE_GUIDE,
    ...options,
}));

const PARAMETER_INPUT_GUIDES: Record<string, IntegrationConnectGuideTemplate> = {
    Shopify: {
        copyKey: 'shopify',
        steps: [
            {
                key: 'openSettings',
                image: SHOPIFY_IMAGE_GUIDES[0],
                showCopy: false,
            },
            {
                key: 'openDomains',
                image: SHOPIFY_IMAGE_GUIDES[1],
                showCopy: false,
            },
        ],
    },
    BigCommerce: {
        copyKey: 'bigCommerce',
        steps: createPlaceholderSteps(['openControlPanel', 'findStoreHash', 'findAccountUuid'], { showImage: false }),
    },
    Feishu: {
        copyKey: 'feishu',
        steps: [
            {
                key: 'openPlatform',
                image: FEISHU_IMAGE_GUIDES[0],
            },
            {
                key: 'copyCredentials',
                image: FEISHU_IMAGE_GUIDES[1],
            },
            {
                key: 'addPermissions',
                image: FEISHU_IMAGE_GUIDES[2],
            },
        ],
    },
    DingTalk: {
        copyKey: 'dingtalk',
        steps: [
            {
                key: 'createInternalApp',
                image: DINGTALK_IMAGE_GUIDES[0],
            },
            {
                key: 'configureCallback',
                image: DINGTALK_IMAGE_GUIDES[1],
            },
            {
                key: 'enablePermissions',
                image: DINGTALK_IMAGE_GUIDES[2],
            },
        ],
    },
    Attribuly: {
        copyKey: 'attribuly',
        action: {
            href: 'https://app.attribuly.com/',
            labelKey: 'website.integrationConnectGuides.attribuly.openAttribuly',
        },
        steps: createPlaceholderSteps(['openSettings', 'createKey', 'pasteKey'], { showImage: false }),
    },
    Ahrefs: {
        copyKey: 'ahrefs',
        steps: [
            {
                key: 'openApiKeys',
                image: AHREFS_IMAGE_GUIDES[0],
            },
            {
                key: 'createKey',
                image: AHREFS_IMAGE_GUIDES[1],
            },
            {
                key: 'pasteKey',
                image: AHREFS_IMAGE_GUIDES[2],
            },
        ],
    },
    Omnisend: {
        copyKey: 'omnisend',
        steps: [
            {
                key: 'openApiKeys',
                image: OMNISEND_IMAGE_GUIDES[0],
            },
            {
                key: 'createKey',
                image: OMNISEND_IMAGE_GUIDES[1],
            },
            {
                key: 'copyKey',
                image: OMNISEND_IMAGE_GUIDES[2],
            },
        ],
    },
    Okendo: {
        copyKey: 'okendo',
        steps: [
            {
                key: 'openCredentials',
                image: OKENDO_IMAGE_GUIDES[0],
            },
            {
                key: 'copyUserIdAndApiKey',
                image: OKENDO_IMAGE_GUIDES[1],
            },
            {
                key: 'pasteCredentials',
                image: OKENDO_IMAGE_GUIDES[2],
            },
        ],
    },
    PrestaShop: {
        copyKey: 'prestashop',
        steps: [
            {
                key: 'openWebservice',
                image: PRESTASHOP_IMAGE_GUIDES[0],
            },
            {
                key: 'createKey',
                image: PRESTASHOP_IMAGE_GUIDES[1],
            },
            {
                key: 'copyKey',
                image: PRESTASHOP_IMAGE_GUIDES[2],
            },
        ],
    },
    GoAffPro: {
        copyKey: 'goaffpro',
        steps: [
            {
                key: 'openApiKeys',
                image: GOAFFPRO_IMAGE_GUIDES[0],
            },
            {
                key: 'copyAdminAccessToken',
                image: GOAFFPRO_IMAGE_GUIDES[1],
            },
            {
                key: 'pasteAdminAccessToken',
                image: GOAFFPRO_IMAGE_GUIDES[2],
            },
        ],
    },
    UpPromote: {
        copyKey: 'uppromote',
        steps: [
            {
                key: 'openIntegrationSettings',
                image: UPPROMOTE_IMAGE_GUIDES[0],
            },
            {
                key: 'getApiKey',
                image: UPPROMOTE_IMAGE_GUIDES[1],
            },
            {
                key: 'pasteApiKey',
                image: UPPROMOTE_IMAGE_GUIDES[2],
            },
        ],
    },
    Loox: {
        copyKey: 'loox',
        steps: [
            {
                key: 'openApiKeys',
                image: LOOX_IMAGE_GUIDES[0],
            },
            {
                key: 'copyCredentials',
                image: LOOX_IMAGE_GUIDES[1],
            },
            {
                key: 'pasteCredentials',
                image: LOOX_IMAGE_GUIDES[2],
            },
        ],
    },
    TrustWILL: {
        copyKey: 'trustwill',
        steps: [
            {
                key: 'openDeveloperTools',
                image: TRUSTWILL_IMAGE_GUIDES[0],
            },
            {
                key: 'copyApiKey',
                image: TRUSTWILL_IMAGE_GUIDES[1],
            },
            {
                key: 'pasteApiKey',
                image: TRUSTWILL_IMAGE_GUIDES[2],
            },
        ],
    },
    NoxInfluencer: {
        copyKey: 'noxinfluencer',
        action: {
            href: 'https://www.noxinfluencer.com/skills/dashboard',
            labelKey: 'website.integrationConnectGuides.noxinfluencer.openSkillsDashboard',
        },
        steps: createPlaceholderSteps(['openSkillsDashboard', 'createSkillsApiKey', 'pasteSkillsApiKey'], { showImage: false }),
    },
    Figma: {
        copyKey: 'figma',
        steps: createPlaceholderSteps(['openSettings', 'createToken', 'pasteToken'], { showImage: false }),
    },
    Semrush: {
        copyKey: 'semrush',
        steps: createPlaceholderSteps(['openSubscription', 'copyApiKey', 'pasteApiKey'], { showImage: false }),
    },
    WooCommerce: {
        copyKey: 'wooCommerce',
        steps: createPlaceholderSteps(['openRestApi', 'createKey', 'pasteCredentials'], { showImage: false }),
    },
    Magento: {
        copyKey: 'magento',
        steps: createPlaceholderSteps(['openIntegrations', 'activateIntegration', 'pasteAccessToken'], { showImage: false }),
    },
};

export const getIntegrationConnectGuide = (toolName?: string | null): IntegrationConnectGuideConfig | null => {
    if (!toolName) {
        return null;
    }

    const parameterInputGuide = PARAMETER_INPUT_GUIDES[toolName];
    if (parameterInputGuide) {
        return {
            flow: 'parameterInput',
            toolName,
            ...parameterInputGuide,
        };
    }

    return null;
};

export const getDingTalkMcpConnectGuide = (): IntegrationConnectGuideConfig => ({
    flow: 'parameterInput',
    copyKey: 'dingtalkMcp',
    toolName: 'DingTalk MCP',
    action: {
        href: 'https://mcp.dingtalk.com/',
        labelKey: 'website.integrationConnectGuides.dingtalkMcp.openMarketplace',
    },
    steps: [
        {
            key: 'chooseService',
            image: DINGTALK_MCP_IMAGE_GUIDES[0],
        },
        {
            key: 'enableService',
            image: DINGTALK_MCP_IMAGE_GUIDES[1],
        },
        {
            key: 'copyConfiguration',
            image: DINGTALK_MCP_IMAGE_GUIDES[2],
        },
    ],
});

export const getIntegrationConnectGuideModalWidth = (toolName?: string | null, fallback = 'max-w-md') => {
    const guide = getIntegrationConnectGuide(toolName);
    if (!guide) {
        return fallback;
    }

    return guide.steps.length >= 3 ? 'max-w-[760px]' : 'max-w-[640px]';
};

interface IntegrationConnectGuideProps {
    toolName?: string | null;
    guide?: IntegrationConnectGuideConfig | null;
}

export default function IntegrationConnectGuide({
    toolName,
    guide = getIntegrationConnectGuide(toolName),
}: IntegrationConnectGuideProps) {
    const { t } = useTranslation();
    const [previewImage, setPreviewImage] = useState<GuideImage | null>(null);

    if (!guide) {
        return null;
    }

    const baseKey = `website.integrationConnectGuides.${guide.copyKey}`;
    const steps = guide.steps.map((step) => ({
        key: step.key,
        image: step.image,
        showCopy: step.showCopy !== false,
        showImage: step.showImage !== false,
        title: step.showCopy === false ? '' : t(`${baseKey}.steps.${step.key}.title`, { tool: guide.toolName }),
        description: step.showCopy === false ? '' : t(`${baseKey}.steps.${step.key}.description`, { tool: guide.toolName }),
    }));
    const stepGridClassName = steps.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3';

    return (
        <>
            <section className="rounded-2xl border border-[#E6EAF3] bg-[#F7F9FC] p-4 sm:p-5">
                <div className="flex flex-col items-start gap-3 sm:flex-row">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#DDE3EE] bg-white text-[#1A162F] shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
                        <Info size={16} weight="regular" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-semibold text-[#1A162F]">
                            {t(`${baseKey}.title`, { tool: guide.toolName })}
                        </h4>
                        <p className="mt-1 text-sm leading-6 text-[#666F8D]">
                            {t(`${baseKey}.description`, { tool: guide.toolName })}
                        </p>
                    </div>
                    {guide.action ? (
                        <a
                            href={guide.action.href}
                            target="_blank"
                            rel="noreferrer"
                            className="shrink-0 rounded-lg border border-[#DDE3EE] bg-white px-3 py-2 text-xs font-semibold text-[#315EFB] transition-colors hover:border-[#B9C8F5] hover:bg-[#F8FAFF]"
                        >
                            {t(guide.action.labelKey)}
                        </a>
                    ) : null}
                </div>

                {steps.length > 0 ? (
                    <ol className={`mt-4 grid gap-3 ${stepGridClassName}`}>
                        {steps.map((step, index) => (
                            <li
                                key={`${guide.copyKey}-step-${step.key}`}
                                className={`flex flex-col rounded-xl border border-[#DDE3EE] bg-white p-3 shadow-[0_8px_24px_rgba(15,23,42,0.04)] ${step.showCopy && step.showImage ? 'min-h-[260px]' : ''}`}
                            >
                                <div className="mb-2 flex items-center justify-between">
                                    <span className="inline-flex rounded-full bg-[#EEF2FF] px-2.5 py-1 text-[11px] font-semibold text-[#1A162F]">
                                        {t('website.connectShopify.stepLabel', { count: index + 1 })}
                                    </span>
                                </div>
                                {step.showCopy ? (
                                    <>
                                        <h5 className="text-sm font-semibold text-[#1A162F]">
                                            {step.title}
                                        </h5>
                                        <p className="mt-1 min-h-[48px] text-xs leading-5 text-[#666F8D]">
                                            {step.description}
                                        </p>
                                    </>
                                ) : null}

                                {step.showImage ? (
                                    step.image.src ? (
                                        <button
                                            type="button"
                                            onClick={() => setPreviewImage(step.image)}
                                            className={`group relative block aspect-video w-full overflow-hidden rounded-lg border border-[#E6EAF3] bg-[#F8FAFC] text-left ${step.showCopy ? 'mt-auto' : 'mt-1'}`}
                                        >
                                            <img
                                                src={step.image.src}
                                                alt={t(step.image.altKey)}
                                                className="h-full w-full object-contain"
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center bg-[#1A162F]/0 transition-colors duration-200 group-hover:bg-[#1A162F]/10">
                                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-[#1A162F] opacity-0 shadow-sm transition-opacity duration-200 group-hover:opacity-100">
                                                    <MagnifyingGlassPlus size={18} weight="regular" />
                                                </span>
                                            </div>
                                        </button>
                                    ) : (
                                        <div className={`flex aspect-video w-full flex-col items-center justify-center rounded-lg border border-dashed border-[#CBD5E1] bg-[#F8FAFC] px-4 text-center ${step.showCopy ? 'mt-auto' : 'mt-1'}`}>
                                            <ImageSquare size={28} weight="regular" className="mb-2 text-[#666F8D]" />
                                            <p className="text-xs font-medium text-[#666F8D]">
                                                {t('website.integrationConnectGuides.referenceImageComingSoon')}
                                            </p>
                                        </div>
                                    )
                                ) : null}
                            </li>
                        ))}
                    </ol>
                ) : null}
            </section>

            {previewImage?.src ? createPortal(
                <div
                    className="fixed inset-0 flex items-center justify-center bg-[#0F172A]/70 p-4 backdrop-blur-sm"
                    style={{ zIndex: WEBSITE_OVERLAY_LAYERS.imagePreview }}
                    onClick={() => setPreviewImage(null)}
                >
                    <div
                        className="relative w-full max-w-6xl rounded-2xl bg-white p-3 shadow-2xl sm:p-4"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={() => setPreviewImage(null)}
                            className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-[#666F8D] shadow-sm transition-colors hover:text-[#1A162F]"
                            aria-label={t('website.connectShopify.closeImagePreview')}
                        >
                            <X size={18} weight="regular" />
                        </button>

                        <div className="overflow-hidden rounded-xl border border-[#E6EAF3] bg-[#F8FAFC]">
                            <img
                                src={previewImage.src}
                                alt={t(previewImage.altKey)}
                                className="mx-auto block max-h-[80vh] w-auto max-w-full object-contain"
                            />
                        </div>
                    </div>
                </div>,
                document.body
            ) : null}
        </>
    );
}
